/**
 * Das Reisebuch — wer den Knopf sieht, was er gerade sagt, wohin ein Klick führt.
 *
 * ## Was das Buch ist
 *
 * Ein gesetztes PDF der **gefahrenen** Reise: Titelseite mit gezeichneter
 * Karte, Kapitel als Buchkapitel, je Tag die Stationen mit Tagebuch und Fotos,
 * am Ende Bilanz und Kosten. Erzeugt wird es auf dem Server
 * (`POST /trips/{id}/reisebuch`), abgerufen unter
 * `GET /trips/{id}/reisebuch.pdf`.
 *
 * ## Einmal je Klick — und Neu setzen als bewusster zweiter Schritt
 *
 * Der Server setzt das Buch auf einen POST **einmal** und danach nicht von
 * selbst wieder: Liegt schon eines, antwortet er mit 409 und rührt die Datei
 * nicht an. Das bleibt so — ein zweiter Klick setzt nichts Zweites. Bis zum
 * 7.10.2026 war das zugleich das letzte Wort; seit Stufe A des Vertrags
 * „Reisebuch als Magazin“ gibt es den ausdrücklichen Weg zurück:
 * `DELETE /trips/{id}/reisebuch` räumt beide Fassungen und den Beizettel weg
 * (die Umschlagbilder bleiben im Zwischenspeicher des Servers und kosten beim
 * nächsten Satz nichts), und ein frischer POST setzt neu. Beliebig oft.
 *
 * In der Oberfläche heißt das **Neu setzen** — ein eigener Knopf neben den
 * beiden Öffnen-Knöpfen, mit Rückfrage. Gelöscht wird dabei erst, wenn in
 * der Auswahlmaske wirklich gesetzt wird, und nicht schon beim Klick: Wer die
 * Maske abbricht, behält sein Buch. Und die Maske braucht das liegende Buch
 * noch, denn sie belegt sich mit dessen letzter Wahl vor
 * (`GET /reisebuch/stand`) — nach einem DELETE gäbe es die nicht mehr.
 *
 * Gewählt wird vor dem Setzen: die **Vorlage** (warm, klar, alpin oder
 * klassisch — Schrift, Farbe, Umschlagbild), die **Betriebsart** (das ganze
 * Buch oder eine Auswahl von Hand) und die **Collage** an den Kapitelenden.
 * Die Auswahl ist eine Liste dessen, was **fehlen** soll, und nicht dessen,
 * was drin ist (siehe `Auswahl`). Welche Vorlagen es gibt, sagt der Server
 * (`GET /reisebuch/vorlagen`) — die Liste steht hier ausdrücklich nicht. Was
 * hier steht, sind die Farbtupfer der Vorschau (`palette`), und die sind
 * Zier: Kommt ein Name dazu, den sie nicht kennen, fehlt der Tupfer und sonst
 * nichts.
 *
 * Gesetzt wird in **zwei Fassungen** zugleich: für den Bildschirm
 * (Einzelseiten, symmetrische Ränder) und für den Druck (Doppelseiten mit
 * Bundsteg, Seitenzahl außen). Beide liegen danach nebeneinander und haben je
 * einen Öffnen-Knopf (`oeffnungsknoepfe`).
 *
 * ## Wo es steht: Erinnern › Reisebuch (seit dem 7.10.2026)
 *
 * Bis zum 7.10.2026 hingen Knopf, Zeile und Auswahlmaske an der Reisezeile in
 * *Meine Reisen*. Florian fand das auf der Startseite komisch, und er hatte
 * recht: Das Buch ist eine Sache **einer** Reise, und die Übersicht ist der
 * Ort, an dem man zwischen Reisen wählt. Seitdem hat es einen eigenen Reiter
 * hinter „Teilen“ in der Phase Erinnern (`ReisebuchPanel.svelte`); die Zeile
 * trägt höchstens noch einen kleinen Verweis dorthin.
 *
 * ## Gesetzt wird im Hintergrund
 *
 * Mit zwei Fassungen und 254 Fotos dauerte der Satz länger als die 60
 * Sekunden, nach denen nginx kappt — der Browser bekam 504, das Buch entstand
 * trotzdem. Seitdem antwortet `POST /reisebuch` sofort mit **202** und einem
 * Laufstand (`{laeuft, schritt, von, bis, hinweis}`), und
 * `GET /reisebuch/stand` liefert `{buch, lauf}`. Das Panel fragt alle drei
 * Sekunden nach (`ABFRAGE_MS`), bis der Lauf steht, und zeigt dazwischen den
 * wirklichen Schritt (`fortschritt`). Die Lesart der Antworten steht hier —
 * `laufAus`, `standantwort`, `nachLauf` —, weil eine verwechselte Form im
 * Markup still einen Knopf falsch schaltet.
 *
 * ## Warum diese Datei existiert
 *
 * Dieselbe Begründung wie bei `mitplanen.ts` und `reiseabschluss.ts`: Die Tests
 * dieses Projekts laufen ohne Browser, und was in einer `.svelte`-Datei steht,
 * ist von dort unerreichbar. Hier steht ausgerechnet die Art Regel, die still
 * falsch wird — sie schaltet einen Knopf, und ein falsch geschalteter Knopf
 * sieht richtig aus. Er verspricht dann eine Datei, die der Server diesem
 * Betrachter mit 404 verweigert.
 *
 * ## Nur an eigenen, abgeschlossenen Reisen
 *
 * Zwei Bedingungen, zwei verschiedene Gründe.
 *
 * **Eigen**, weil beide Wege im Server `_owned_trip` prüfen: Ein Mitplaner
 * bekommt 404, und zwar mit Absicht — das Buch ist das private Gegenstück zur
 * geteilten Seite und fasst in einer Datei zusammen, was sonst über fünf
 * Ansichten verteilt liegt (Notizen, Kosten, zurückgehaltene Einträge, jedes
 * Foto). Wer eine fremde Reise vollständig in eine Datei gießen darf, nimmt sie
 * beim Hinausgehen mit. Die Frage, wem die Reise gehört, beantwortet
 * `istFremd` — also der Server über `mitplaner_von` und nicht wir.
 *
 * **Abgeschlossen**, und das ist eine Entscheidung dieser Oberfläche und keine
 * des Servers: Der Server setzt das Buch jederzeit. Es ist aber das Buch der
 * gefahrenen Reise — eine halbe Reise ergibt ein halbes Buch mit leeren
 * Kapiteln und einer Bilanz ohne Ist-Werte, und das sähe nach einem Fehler des
 * Satzes aus statt nach einer Reise, die noch läuft. Wer es vorher will,
 * schließt ab; der Haken ist mit einem Klick umkehrbar und sperrt nichts.
 *
 * ## Was hier bewusst NICHT steht
 *
 * Eine Abfrage beim Laden der **Übersicht**. Dort wäre sie ein Abruf **je
 * Zeile** — bei fünfzehn gefahrenen Reisen fünfzehn Anfragen für
 * Beschriftungen, die niemand angefordert hat. `GET /trips` sagt nichts über
 * die Ablage, und das soll es auch nicht. Seit das Buch im Reiter steht, gibt
 * es die Frage trotzdem — aber genau einmal, beim Öffnen des Reiters einer
 * Reise (`GET /reisebuch/stand`), und das ist die Abfrage, die jemand mit dem
 * Klick auf „Reisebuch“ angefordert hat.
 *
 * Der Preis dafür war früher ein zweiter, überflüssiger Satz derselben Reise.
 * Seit der Einmal-Regel ist es etwas Besseres: Der 409 trägt in `detail` kein
 * Bedauern, sondern **den Beizettel des liegenden Buchs** — Vorlage, Seiten,
 * Bytes, Zeitpunkt, Illustration. Das ist genau die Auskunft, für die es hier
 * einmal keinen Weg gab, und sie kostet keine Anfrage im Voraus, sondern fällt
 * bei dem Klick an, der sie braucht. `nachAblehnung` macht daraus den Zustand
 * `liegt`: Wer auf ein Buch aus einer anderen Sitzung stößt, sieht danach
 * seinen Öffnen-Knopf samt Zeile und keine Fehlermeldung.
 *
 * Seit dem 7.10.2026 gibt es dazu einen zweiten, ebenso sparsamen Weg:
 * `GET /reisebuch/stand` liefert den Beizettel — aber nur, wenn die
 * Auswahlmaske einer Reise aufgeht, und nie beim Laden der Liste. Die Maske
 * braucht ihn zum Vorbelegen; fällt dabei ein liegendes Buch an, erfährt es
 * die Zeile über `nachStand` gleich mit.
 *
 * Reine Funktionen — keine Uhr, kein Netz, kein DOM.
 */

import { datumZeit } from './datum';
import { istFremd, type Reisebeteiligung } from './mitplanen';
import { anzahl } from './planner/sprache';
import { istAbgeschlossen, type MitAbschluss } from './reiseabschluss';
import { groesse } from './tagebuch';

/**
 * Was der Server nach dem Erzeugen meldet.
 *
 * Genug, um nach dem Klick etwas Wahres anzuzeigen, ohne die Datei zu holen.
 * `erzeugt_am` ist ein örtlicher Zeitstempel ohne Zone (`2026-08-24T14:23:11`)
 * und wird deshalb wie überall hier **zerlegt** statt umgerechnet — siehe
 * `datum.ts`.
 */
export interface Reisebuchstand {
  seiten: number;
  bytes: number;
  erzeugt_am?: string | null;
  /**
   * Die gesetzte Vorlage — `warm`, `klar`, `alpin`, `klassisch`.
   *
   * Absichtlich `string` und kein Aufzählungstyp: Die Liste gehört dem Server,
   * und ein hier festgeschriebener Satz von vier Namen wäre die Sorte Kopie,
   * die still veraltet. Was der Name bedeutet, sagt `vorlagentitel` — und wenn
   * er ihn nicht kennt, steht eben der Name da.
   */
  vorlage?: string | null;
  /** Woher das Umschlagbild kam: `vektor`, `foto` oder `keine`. */
  illustration?: string | null;
  /** `vollstaendig` oder `auswahl` — fehlt bei Büchern von vor dem 7.10.2026. */
  betriebsart?: Betriebsart | null;
  /**
   * Welche Fassungen auf dem Server liegen.
   *
   * Fehlt die Angabe, ist es ein Buch von vor den zwei Fassungen: Dann liegt
   * genau eine Datei da, und der Server liest sie als Bildschirmfassung.
   */
  fassungen?: Fassung[] | null;
  /** Die Wahl, mit der gesetzt wurde — nur in der Betriebsart `auswahl`. */
  auswahl?: Auswahl | null;
  /**
   * Je Fassung Seiten und Bytes — `reisebuch.stand` im Server.
   *
   * Die Seiten fehlen bei Büchern, deren Beizettel sie nicht kennt; dann steht
   * für die Bildschirmfassung `seiten` ein, für die Druckfassung nichts.
   */
  je_fassung?: Partial<Record<Fassung, Fassungsangabe>> | null;
  /**
   * Collage an den Kapitelenden — an oder aus.
   *
   * In Stufe A merkt der Server sich nur die Wahl; gesetzt wird die Collage
   * erst mit dem Magazin-Satz (Stufe B5).
   */
  collage?: boolean | null;
  /**
   * Die Titelfelder, mit denen gesetzt wurde (Korrekturpaket 1, 9.10.2026):
   * Vorspann über dem Titel, der Buchtitel selbst, ein Untertitel darunter.
   * Fehlen sie, gab es beim Setzen keine — dann nimmt der Server den
   * Reisenamen, und die Maske schlägt ihn zerlegt vor (`titelfelder`).
   */
  vorspann?: string | null;
  titel?: string | null;
  untertitel?: string | null;
  /**
   * Der Eintrag, der das Buch als **Fazit** beschließt (Korrekturpaket 2,
   * Punkt 12, 10.10.2026): eine Kennung aus dem Tagebuch, `0` für „kein
   * Fazit“. Fehlt die Angabe, galt die Vorgabe des Servers — der letzte
   * lange Eintrag mit Titel — oder das Buch ist älter als das Fazit-Kapitel.
   */
  fazit_eintrag_id?: number | null;
  /** Der Titel des Fazit-Eintrags, wenn der Server ihn mitschickt. */
  fazit_titel?: string | null;
  /**
   * Woher die Großzitate kamen (Punkt 10): `Lotte`, wenn Lotte sie aus dem
   * Text gewählt hat, `Regel`, wenn der Satz ohne sie auskommen musste und
   * nach den festen Filtern gewählt hat. Fehlt bei älteren Büchern.
   */
  zitate_quelle?: string | null;
}

/* ══ Fassungen, Betriebsart, Auswahl ══════════════════════════════════════ */

/**
 * Die zwei Fassungen eines Buchs.
 *
 * `bildschirm`: Einzelseiten mit symmetrischen Rändern. `druck`: Doppelseiten
 * mit Bundsteg innen und Seitenzahl außen, Kapitel beginnen rechts. Beide
 * entstehen bei **einem** Satz; abgerufen wird je Fassung
 * (`GET /reisebuch.pdf?fassung=…`).
 */
export type Fassung = 'bildschirm' | 'druck';

/** Was der Server je Fassung meldet. */
export interface Fassungsangabe {
  seiten?: number | null;
  bytes?: number | null;
}

/** Die Fassungen in der Reihenfolge, in der ihre Knöpfe stehen. */
export const FASSUNGEN: readonly Fassung[] = ['bildschirm', 'druck'];

/** Die Beschriftung des Öffnen-Knopfs je Fassung. */
export const FASSUNGSTITEL: Readonly<Record<Fassung, string>> = {
  bildschirm: 'Bildschirm',
  druck: 'Druck'
};

/** Der Titel am Knopf — er sagt, wofür die Fassung gedacht ist. */
export const FASSUNGSZWECK: Readonly<Record<Fassung, string>> = {
  bildschirm: 'Das Reisebuch zum Lesen am Bildschirm öffnen — Einzelseiten',
  druck: 'Das Reisebuch zum Drucken öffnen — Doppelseiten mit Bundsteg'
};

/**
 * Welche Öffnen-Knöpfe an einem liegenden Buch stehen.
 *
 * Was der Beizettel nennt, in fester Reihenfolge. Nennt er **nichts**, ist es
 * ein Buch von vor dem 7.10.2026: Es liegt als eine Datei da, die der Server
 * als Bildschirmfassung liest — ein Druck-Knopf daran holte einen 404, und
 * der hieße für die Zeile „das Buch ist weg“, obwohl es daliegt.
 *
 * Der Beizettel darf die Fassungen als Liste oder als Objekt (Fassung →
 * Angaben) führen; gezählt werden die Namen.
 */
export function oeffnungsknoepfe(stand: Reisebuchstand | null | undefined): Fassung[] {
  const roh: unknown = stand?.fassungen;
  const namen = Array.isArray(roh)
    ? roh
    : roh && typeof roh === 'object'
      ? Object.keys(roh)
      : [];
  const da = FASSUNGEN.filter((f) => namen.includes(f));
  return da.length ? da : ['bildschirm'];
}

/**
 * Das ganze Buch oder eine Auswahl von Hand.
 *
 * `vollstaendig`: alle Einträge in voller Länge, alle Fotos. `auswahl`: was
 * die Maske abgewählt hat, fehlt — gekürzt wird dabei nichts, ein Text steht
 * ganz drin oder gar nicht (Florian, 7.10.: sein Text bleibt sein Text).
 */
export type Betriebsart = 'vollstaendig' | 'auswahl';

/**
 * Die sieben Themen, die sich im Buch ab- und anschalten lassen.
 *
 * `true` heißt **drin** — dieselbe Richtung wie die Haken der Maske. Die
 * Schlüssel sind die des Servers (`ReisebuchWunsch.auswahl.themen`,
 * `main.ReisebuchThemen`) und stehen deshalb wortgleich hier.
 *
 * `planungsnotizen` kam am 8.10.2026 dazu (Stufe B6): die Notizen aus der
 * Planung unter dem Stationskopf. Sie sind Text für die Fahrt — Buchungen,
 * Codes, Parken —, und in Reise 19 sind das 238.000 Zeichen samt
 * Buchungsnummern, PIN und Hauscode. Seit dem 9.10.2026 (Korrekturpaket 1,
 * Punkt 5) sind sie in **beiden** Betriebsarten von Haus aus aus — auch im
 * ganzen Buch, wo von den Themen sonst keines wirkt; einschalten lässt sie
 * der Haken in der Maske. Siehe `THEMEN_VORGABE`.
 *
 * Dasselbe gilt seit dem 10.10.2026 für `kosten` (Korrekturpaket 2, Punkt
 * 11): Die Kostenseite der Bilanz und die Beträge in der Randspalte sind in
 * beiden Betriebsarten von Haus aus aus — meist zu nüchtern fürs Buch.
 */
export interface Themen {
  kosten: boolean;
  messwerte: boolean;
  fahrzeug: boolean;
  abstecher: boolean;
  karte_je_kapitel: boolean;
  fusswege: boolean;
  planungsnotizen: boolean;
}

/** Die Themen in der Reihenfolge der Maske, mit Namen und einem Satz. */
export const THEMEN: readonly { schluessel: keyof Themen; titel: string; satz: string }[] = [
  { schluessel: 'kosten', titel: 'Kosten', satz: 'Kostenseite und Kostenzeilen — meist zu nüchtern fürs Buch' },
  { schluessel: 'messwerte', titel: 'Messwerte', satz: 'Temperatur und Wetter am Rand' },
  { schluessel: 'fahrzeug', titel: 'Fahrzeug', satz: 'Akku, Laden, Kilometerstand am Rand' },
  { schluessel: 'abstecher', titel: 'Abstecher', satz: 'Die Stationen abseits der Route' },
  { schluessel: 'karte_je_kapitel', titel: 'Karte je Kapitel', satz: 'Die kleine Übersicht am Kapitelanfang' },
  { schluessel: 'fusswege', titel: 'Fußwege', satz: 'Die Fußwegzeilen an den Stationen' },
  {
    schluessel: 'planungsnotizen',
    titel: 'Planungsnotizen',
    satz: 'Die Notizen an den Stationen: Buchungen, Codes, Parken, Hinweise'
  }
];

/**
 * Die Vorgabe einer frischen Auswahl: alles drin bis auf die
 * Planungsnotizen und — seit dem 10.10.2026 — die Kosten.
 *
 * Die Ausnahme ist eine Entscheidung (Florian, 8.10.2026) und kein
 * Versehen: Ein Buch ist zum Zeigen, und Buchungsnummern und Türcodes
 * gehören da nicht hinein, nur weil niemand daran gedacht hat, sie
 * abzuwählen. Seit dem 9.10.2026 gilt das auch fürs ganze Buch: Der Server
 * wendet den Schalter `planungsnotizen` in beiden Betriebsarten an (die
 * übrigen sechs Themen weiter nur in `auswahl`), und die Maske zeigt ihn
 * deshalb auch unter „Das ganze Buch“.
 *
 * Am 10.10.2026 (Korrekturpaket 2, Punkt 11) kamen die **Kosten** dazu:
 * dieselbe Vorgabe, dieselbe Wirkung in beiden Betriebsarten, derselbe
 * Haken unter dem Umfang. Florian fand die Kostenseite im fertigen Buch zu
 * nüchtern; wer sie will, schaltet sie ein.
 */
export const THEMEN_VORGABE: Readonly<Themen> = {
  kosten: false,
  messwerte: true,
  fahrzeug: true,
  abstecher: true,
  karte_je_kapitel: true,
  fusswege: true,
  planungsnotizen: false
};

/**
 * Die Themen, die in **beiden** Betriebsarten wirken und von Haus aus aus
 * sind — die Maske zeigt ihre Haken beim ganzen Buch unter dem Umfang.
 * Der Server wendet von den Themen im ganzen Buch genau diese an.
 */
export const THEMEN_IMMER: readonly (keyof Themen)[] = ['planungsnotizen', 'kosten'];

/**
 * Der Hinweissatz der Maske zu den Themen, die von Haus aus aus sind — in
 * beiden Betriebsarten derselbe, deshalb hier und nicht zweimal im Markup.
 * Bis zum 10.10.2026 hieß er `PLANUNGSNOTIZEN_SATZ` und nannte nur die
 * Planungsnotizen.
 */
export const VORGABE_AUS_SATZ =
  'Die Planungsnotizen (Buchungen, Codes, Parken) und die Kosten gehören selten ins Buch und sind deshalb aus. Willst du sie trotzdem drin haben, schalte sie hier ein.';

/**
 * Der Hinweis zu den Großzitaten (Korrekturpaket 2, Punkt 10). Die Zitate
 * wählt seit dem 10.10.2026 Lotte aus dem Text; ein im Tagebuch markiertes
 * Zitat übersteuert sie.
 */
export const ZITATE_SATZ =
  'Die Großzitate wählt Lotte aus deinem Text; ein Zitat, das du im Tagebuch markierst, hat Vorrang.';

/**
 * Die Auswahl — als Liste dessen, was **fehlen** soll.
 *
 * Ausschluss- und nicht Einschlusslisten, und das ist die tragende
 * Entscheidung des Vertrags: Ein Foto, das nach der Wahl dazukommt, ist drin,
 * bis es jemand abwählt. Eine Einschlussliste ließe es beim nächsten
 * Neu-Setzen still weg — eine alte Wahl würde mit jedem neuen Foto ein
 * bisschen falscher. So verliert sie nichts.
 *
 * `kapitel_aus` führt die **Marken** (Kapitelnamen), nicht Nummern: Die
 * Stationen bekommen beim Speichern neue Kennungen, ein Name bleibt.
 */
export interface Auswahl {
  kapitel_aus: string[];
  fotos_aus: number[];
  eintraege_aus: number[];
  themen: Themen;
}

/** Eine Auswahl, in der nichts fehlt. */
export function leereAuswahl(): Auswahl {
  return { kapitel_aus: [], fotos_aus: [], eintraege_aus: [], themen: { ...THEMEN_VORGABE } };
}

/**
 * Eine Auswahl aus dem Netz lesen — oder die leere.
 *
 * Der Beizettel ist JSON vom Server und nichts davon zugesichert. Was keine
 * Liste ist, wird eine leere; was in einer Liste nicht passt (eine Zahl als
 * Marke, ein Text als Kennung), fällt heraus; ein fehlendes Thema bekommt
 * seine Vorgabe (`THEMEN_VORGABE`). Für fünf Themen heißt das **drin** —
 * dieselbe Richtung wie bei neuen Fotos: Im Zweifel steht etwas im Buch,
 * statt still zu fehlen. Planungsnotizen und Kosten sind die Ausnahme: Ein
 * Beizettel von vor dem 8.10.2026 kennt den Schalter Planungsnotizen nicht,
 * und wer dann „Meine Auswahl“ neu setzt, soll seine Buchungsnummern nicht
 * wegen eines fehlenden Felds im Buch finden. Was ausdrücklich im
 * Beizettel steht, gilt — auch ein `true`; ein älteres Buch mit Kosten
 * findet deshalb beim Neu-Setzen seinen Haken an wieder.
 */
export function auswahlAus(roh: unknown): Auswahl {
  const leer = leereAuswahl();
  if (!roh || typeof roh !== 'object' || Array.isArray(roh)) return leer;
  const d = roh as Record<string, unknown>;
  const worte = (x: unknown) =>
    Array.isArray(x)
      ? [...new Set(x.filter((w): w is string => typeof w === 'string' && !!w.trim()))]
      : [];
  const zahlen = (x: unknown) =>
    Array.isArray(x)
      ? [...new Set(x.filter((z): z is number => typeof z === 'number' && Number.isInteger(z)))]
      : [];
  const themenRoh =
    d.themen && typeof d.themen === 'object' && !Array.isArray(d.themen)
      ? (d.themen as Record<string, unknown>)
      : {};
  const themen = { ...THEMEN_VORGABE };
  for (const { schluessel } of THEMEN) {
    const wert = themenRoh[schluessel];
    if (typeof wert === 'boolean') themen[schluessel] = wert;
  }
  return {
    kapitel_aus: worte(d.kapitel_aus),
    fotos_aus: zahlen(d.fotos_aus),
    eintraege_aus: zahlen(d.eintraege_aus),
    themen
  };
}

/**
 * Die Foto-Werkzeuge in der Auswahl (Paket 3): Vollseiten, Textfotos und die
 * Reihenfolge je Kapitel (`main.ReisebuchAuswahl`). Anders als die
 * Ausschlusslisten wirken sie in beiden Betriebsarten.
 */
export interface Fotowahl {
  fotos_vollseite?: number[];
  fotos_text?: number[];
  fotos_folge?: Record<string, number[]>;
}

/** Was die Maske beim Setzen weiß. */
export interface Buchwunsch {
  vorlage: string;
  betriebsart: Betriebsart;
  collage: boolean;
  auswahl: Auswahl;
  /** Die Titelfelder — leer oder fehlend heißt: nicht senden. */
  vorspann?: string | null;
  titel?: string | null;
  untertitel?: string | null;
  /**
   * Der Fazit-Eintrag: eine Kennung, `0` für „kein Fazit“, `null` oder
   * fehlend für „automatisch“ — dann geht das Feld nicht mit.
   */
  fazit_eintrag_id?: number | null;
}

/**
 * Der Körper für `POST /trips/{id}/reisebuch` — die Form von
 * `ReisebuchWunsch` im Server.
 */
export interface Reisebuchkoerper {
  vorlage: string;
  betriebsart: Betriebsart;
  collage: boolean;
  /**
   * Seit Paket 3 (10.10.2026) trägt die Auswahl auch die Foto-Werkzeuge —
   * gebaut in `reisebuchauswahl.fotokoerper` und nicht hier: Diese Datei
   * liegt im Hauptbündel (die Reiseliste braucht sie), und was nur die Maske
   * braucht, soll es nicht vergrößern.
   */
  auswahl?: Auswahl & Fotowahl;
  vorspann?: string;
  titel?: string;
  untertitel?: string;
  fazit_eintrag_id?: number;
}

/* ══ Fazit (Korrekturpaket 2, 10.10.2026) ═════════════════════════════════ */

/** Die Vorgabe der Fazit-Wahl, wie die Maske sie nennt. */
export const FAZIT_AUTOMATISCH = 'automatisch (letzter langer Eintrag mit Titel)';

/** Die Wahl „kein Fazit“ — gesendet als `0`. */
export const FAZIT_KEINS = 'kein Fazit';

/**
 * Eine Fazit-Wahl aus dem Netz lesen: eine ganze Zahl ab `0`, sonst `null`
 * (automatisch). Eine negative Zahl oder ein Text ist keine Wahl.
 */
export function fazitwahl(roh: unknown): number | null {
  return typeof roh === 'number' && Number.isInteger(roh) && roh >= 0 ? roh : null;
}

/* ══ Titelfelder (Korrekturpaket 1, 9.10.2026) ════════════════════════════ */

/**
 * Wie lang ein Titelfeld höchstens sein darf — dieselbe Grenze wie in
 * `ReisebuchWunsch` im Server. Länger trägt kein Umschlag.
 */
export const TITEL_HOECHSTENS = 80;

/** Die drei Titelfelder der Maske. */
export interface Titelfelder {
  vorspann: string;
  titel: string;
  untertitel: string;
}

/** Ein Titelfeld bereinigt: Ränder weg, auf die Höchstlänge gekürzt. */
export function titelfeld(roh: unknown): string {
  if (typeof roh !== 'string') return '';
  return roh.trim().slice(0, TITEL_HOECHSTENS).trimEnd();
}

/**
 * Die Vorbelegung der Titelfelder.
 *
 * Hat das liegende Buch Titelfelder (Beizettel), gelten sie — auch ein
 * leerer Untertitel, solange Titel oder Vorspann dastehen: Das war die
 * letzte Wahl. Sonst wird der **Reisename** zerlegt, so wie Florian seine
 * Reisen nennt: „Städte-Tour West: Turin, Genua, Toskana, Rom“ — der Teil
 * vor dem ersten Doppelpunkt wird der Vorspann, der Rest der Titel. Ohne
 * Doppelpunkt (oder mit leerem Teil davor oder dahinter) ist der ganze Name
 * der Titel und der Vorspann leer. Ein
 * Untertitel ergibt sich aus dem Namen nie.
 */
export function titelfelder(
  stand: Reisebuchstand | null | undefined,
  reisename: string | null | undefined
): Titelfelder {
  const ausStand = {
    vorspann: titelfeld(stand?.vorspann),
    titel: titelfeld(stand?.titel),
    untertitel: titelfeld(stand?.untertitel)
  };
  if (ausStand.titel || ausStand.vorspann) return ausStand;

  // Dieselbe Regel wie `reisebuch_umschlag.titel_teilen` im Server: Leerraum
  // zusammenziehen, am ersten Doppelpunkt teilen — aber nur, wenn davor und
  // dahinter etwas steht; sonst bleibt der Name ganz.
  const name = (reisename ?? '').split(/\s+/).filter(Boolean).join(' ');
  const stelle = name.indexOf(':');
  const vorn = stelle >= 0 ? name.slice(0, stelle).trim() : '';
  const hinten = stelle >= 0 ? name.slice(stelle + 1).trim() : '';
  if (vorn && hinten) {
    return { vorspann: titelfeld(vorn), titel: titelfeld(hinten), untertitel: '' };
  }
  return { vorspann: '', titel: titelfeld(name), untertitel: '' };
}

/**
 * Die Vorbelegung der Maske — aus dem Beizettel des liegenden Buchs.
 *
 * Die Auswahl kommt auch dann zurück, wenn das Buch vollständig gesetzt
 * wurde: Der Server legt sie in jeden Beizettel (siehe `buchkoerper`).
 *
 * Die Vorlage bleibt `null`, wenn der Beizettel keine nennt **und** keine
 * Vorgabe übergeben ist; dann wählt die Maske, sobald die Liste da ist. Die
 * Betriebsart ist nur dann `auswahl`, wenn der Beizettel das ausdrücklich
 * sagt — ein Buch von vor dem 7.10.2026 war immer vollständig.
 */
export function wunschAusStand(
  stand: Reisebuchstand | null | undefined,
  vorgabe: string | null = null
): Omit<Buchwunsch, 'vorlage'> & { vorlage: string | null } {
  const vorlage =
    typeof stand?.vorlage === 'string' && stand.vorlage.trim() ? stand.vorlage : vorgabe;
  return {
    vorlage,
    betriebsart: stand?.betriebsart === 'auswahl' ? 'auswahl' : 'vollstaendig',
    collage: stand?.collage === true,
    auswahl: auswahlAus(stand?.auswahl),
    fazit_eintrag_id: fazitwahl(stand?.fazit_eintrag_id)
  };
}

/**
 * Aus dem Wunsch der Maske den Körper des POST.
 *
 * Die Auswahl geht **immer** mit, auch beim ganzen Buch. Wirken tut sie dort
 * nicht — der Server setzt sie nur in der Betriebsart `auswahl` um —, aber er
 * legt sie in den Beizettel (`ReisebuchWunsch` im Server, 7.10.2026): Wer
 * einmal eine Auswahl gebaut, dann probehalber das ganze Buch gesetzt hat,
 * findet beim nächsten Neu-Setzen seine Haken wieder. Eine Auswahl, die
 * beim Umschalten verloren ginge, wäre eine halbe Stunde Arbeit für einen
 * Versuch.
 *
 * Die Listen gehen sortiert und ohne Doppelte hinaus: Sie landen im
 * Beizettel, und dort soll dieselbe Wahl immer gleich aussehen.
 */
export function buchkoerper(w: Buchwunsch): Reisebuchkoerper {
  const a = w.auswahl;
  const koerper: Reisebuchkoerper = {
    vorlage: w.vorlage,
    betriebsart: w.betriebsart,
    collage: w.collage,
    auswahl: {
      kapitel_aus: [...new Set(a.kapitel_aus)].sort((x, y) => x.localeCompare(y, 'de')),
      fotos_aus: [...new Set(a.fotos_aus)].sort((x, y) => x - y),
      eintraege_aus: [...new Set(a.eintraege_aus)].sort((x, y) => x - y),
      themen: { ...THEMEN_VORGABE, ...a.themen }
    }
  };
  // Die Titelfelder nur, wenn etwas drinsteht: Ein leeres Feld heißt „nimm
  // die Vorgabe des Servers“, und ein mitgeschickter Leerstring wäre eine
  // andere Aussage.
  for (const feld of ['vorspann', 'titel', 'untertitel'] as const) {
    const wert = titelfeld(w[feld]);
    if (wert) koerper[feld] = wert;
  }
  // Das Fazit nur bei einer ausdrücklichen Wahl: Fehlt das Feld, nimmt der
  // Server seine Vorgabe. `0` heißt „kein Fazit“ und geht deshalb mit.
  const fazit = fazitwahl(w.fazit_eintrag_id);
  if (fazit !== null) koerper.fazit_eintrag_id = fazit;
  return koerper;
}

/** Eine Vorlage, wie `GET /reisebuch/vorlagen` sie beschreibt. */
export interface Vorlage {
  name: string;
  titel: string;
  beschreibung: string;
}

/**
 * Der Satz, der in der Maske stehen muss.
 *
 * Bis zum 7.10.2026 stand an dieser Stelle die Einmal-Zusage („diese Wahl
 * bleibt“). Sie stimmt nicht mehr: Ein Buch lässt sich neu setzen, beliebig
 * oft. Was stattdessen gesagt werden muss, ist, dass das **nichts kostet** —
 * sonst probiert niemand die Collage oder eine zweite Vorlage aus, und genau
 * dafür gibt es den Weg. Er steht hier und nicht im Markup, weil er eine
 * Zusage ist und keine Beschriftung.
 */
export const NEUSATZ_SATZ =
  'Neu setzen geht jederzeit — die Umschlagbilder bleiben gespeichert, ein zweiter Satz kostet nichts.';

/**
 * Die Rückfrage vor dem Neu-Setzen.
 *
 * Sie sagt, **wann** das liegende Buch verschwindet: nicht beim Klick,
 * sondern erst beim Setzen in der Maske. Wer abbricht, behält es.
 */
export const NEUSATZ_FRAGE =
  'Das Reisebuch neu setzen? Die Auswahl öffnet sich mit deiner letzten Wahl. Das liegende Buch wird erst ersetzt, wenn du dort auf Setzen drückst — brichst du ab, bleibt es liegen.';

/**
 * Die Farbtupfer der Vorschau, je Vorlage.
 *
 * Herkunft: `reisebuch.VORLAGEN` im Backend — dort stehen sie als Satzfarben
 * des PDF. Hier sind sie **Zier** und sonst nichts: drei Punkte neben dem
 * Titel, damit man warm von alpin unterscheidet, ohne den Beschreibungstext zu
 * lesen. Deshalb sind sie hart notiert und werden nicht abgefragt — und
 * deshalb ist ein unbekannter Name kein Fehler, sondern eine Karte ohne
 * Punkte. Die Auswahl selbst kommt zur Laufzeit vom Server.
 */
export const VORLAGENFARBEN: Readonly<Record<string, readonly string[]>> = {
  warm: ['#b4553a', '#3f6b52', '#f6f1e7'],
  // Ein Blau auf hellem Grund — der zweite Wert ist der Grund, nicht ein
  // zweiter Satzton.
  klar: ['#39586b', '#fbfcfd'],
  alpin: ['#2f3a33', '#c9b189'],
  klassisch: ['#000000', '#8f2f24']
};

/** Die Farbtupfer einer Vorlage — leer, wenn der Name unbekannt ist. */
export function palette(name: string | null | undefined): readonly string[] {
  return (name && VORLAGENFARBEN[name]) || [];
}

/**
 * Der Anzeigetitel einer Vorlage — aus der Liste des Servers.
 *
 * Ist die Liste nicht da (der Abruf hat nicht geklappt, oder der Name kommt
 * aus einer älteren Antwort), steht der rohe Name da. Das ist keine schöne,
 * aber eine wahre Auskunft: `warm` ist als Wort verständlich, und ein
 * weggelassener Titel ließe die Zeile so aussehen, als gäbe es keine Vorlage.
 */
export function vorlagentitel(
  name: string | null | undefined,
  vorlagen?: readonly Vorlage[] | null
): string | null {
  if (typeof name !== 'string' || !name.trim()) return null;
  const treffer = (vorlagen ?? []).find((v) => v.name === name);
  return treffer?.titel?.trim() || name;
}

/**
 * Die vier Lagen, in denen der Knopf einer Reise stehen kann.
 *
 * `unbekannt` ist nicht dasselbe wie „es gibt keines": Wir wissen es nicht,
 * und der einzige Weg, es zu erfahren, wäre der Probe-GET, den es aus gutem
 * Grund nicht gibt (siehe Dateikopf). Für den Knopf laufen beide auf dasselbe
 * hinaus — erzeugen —, und deshalb gibt es dafür keinen fünften Zustand.
 */
export type Buchzustand = 'unbekannt' | 'setzt' | 'liegt' | 'fehler';

/** Was diese Sitzung über das Buch einer Reise weiß. */
export interface Bucheintrag {
  zustand: Buchzustand;
  /** Die Kennzahlen der letzten erfolgreichen Erzeugung — sonst `null`. */
  stand?: Reisebuchstand | null;
  /**
   * Der Satz des Servers, unverändert.
   *
   * Er steht ausdrücklich **neben** dem Zustand und nicht in ihm: Nach einem
   * gescheiterten zweiten Satz liegt das alte Buch weiter da (`liegt`) und der
   * Fehler gilt trotzdem. Beides in einen Zustand zu pressen hieße, eines von
   * beiden zu verschweigen.
   */
  fehler?: string | null;
}

/** Der Anfangszustand: Diese Sitzung hat noch nichts erfahren. */
export const BUCH_UNBEKANNT: Bucheintrag = { zustand: 'unbekannt' };

/**
 * Was der Klick auf den Hauptknopf auslöst.
 *
 * `waehlen` und nicht `erzeugen`: Seit der Einmal-Regel steht zwischen Klick
 * und POST die Vorlagenwahl. Ein Knopf, der sofort setzte, nähme die einzige
 * Entscheidung vorweg, die es an dieser Datei zu treffen gibt.
 */
export type Buchhandlung = 'waehlen' | 'oeffnen' | 'nichts';

/**
 * Steht an dieser Reise ein Reisebuch-Knopf?
 *
 * Beide Bedingungen kommen aus den Dateien, die sie sonst schon beantworten —
 * `reiseabschluss.ts` und `mitplanen.ts`. Eine dritte Auslegung von
 * „abgeschlossen" oder „fremd" wäre eine dritte Gelegenheit, dass sie
 * auseinanderlaufen, und hier hinge ein Knopf daran.
 */
export function zeigtReisebuch(t: (MitAbschluss & Reisebeteiligung) | null | undefined): boolean {
  if (!t) return false;
  return istAbgeschlossen(t) && !istFremd(t);
}

/** Der Eintrag einer Reise — oder der Anfangszustand, wenn es keinen gibt. */
export function bucheintrag(e: Bucheintrag | null | undefined): Bucheintrag {
  return e ?? BUCH_UNBEKANNT;
}

/**
 * Die Beschriftung des Hauptknopfs.
 *
 * `setzt …` ist ein ehrlicher Zwischenstand und kein Fortschritt: Der Server
 * meldet keinen, und eine Anzeige, die von 0 auf 90 % kriecht und dort steht,
 * ist eine erfundene Auskunft. Was hier stimmt, ist: Es läuft.
 */
export function buchknopf(zustand: Buchzustand): string {
  if (zustand === 'setzt') return 'setzt …';
  if (zustand === 'liegt') return 'Reisebuch öffnen';
  return 'Reisebuch setzen';
}

/** Läuft gerade ein Satz? Dann ist der Knopf gesperrt — ein zweiter POST setzt dasselbe noch einmal. */
export function buchGesperrt(zustand: Buchzustand): boolean {
  return zustand === 'setzt';
}

/** Was der Hauptknopf in diesem Zustand tut. */
export function buchhandlung(zustand: Buchzustand): Buchhandlung {
  if (zustand === 'setzt') return 'nichts';
  return zustand === 'liegt' ? 'oeffnen' : 'waehlen';
}

/**
 * Die leise Zeile unter dem Knopf:
 * `Warm · 12 Seiten · 3,4 MB · 24.08.2026, 14:23`.
 *
 * Die Vorlage steht **vorn**, seit es eine Wahl gibt: Sie ist die einzige
 * Angabe der Zeile, die man nicht mehr ändern kann, und wer sein Buch nach
 * einem halben Jahr wiederfindet, will zuerst wissen, welche er damals
 * genommen hat. Fehlt sie (ein Buch aus der Zeit vor der Wahl), fängt die
 * Zeile bei den Seiten an wie eh und je.
 *
 * Was nicht brauchbar ist, entfällt — dieselbe Regel wie in `bilanz.ts`: Eine
 * fehlende Angabe ist keine Null, und `0 Seiten` an einem Buch, das gerade
 * erzeugt wurde, wäre eine Falschaussage über eine Datei, die es gibt.
 * `null`, wenn nichts übrig bleibt.
 */
export function buchzeile(
  stand: Reisebuchstand | null | undefined,
  vorlagen?: readonly Vorlage[] | null
): string | null {
  if (!stand) return null;
  const teile: string[] = [];

  const vorlage = vorlagentitel(stand.vorlage, vorlagen);
  if (vorlage) teile.push(vorlage);

  const seiten = stand.seiten;
  if (typeof seiten === 'number' && Number.isFinite(seiten) && seiten > 0) {
    teile.push(anzahl(Math.round(seiten), 'Seite', 'Seiten'));
  }

  const bytes = stand.bytes;
  if (typeof bytes === 'number' && Number.isFinite(bytes)) {
    const g = groesse(bytes);
    if (g) teile.push(g);
  }

  const wann = datumZeit(stand.erzeugt_am);
  if (wann) teile.push(wann);

  return teile.length ? teile.join(' · ') : null;
}

/**
 * Der Klick auf `Reisebuch erzeugen` — der Knopf wird gesperrt.
 *
 * Der bisherige `stand` bleibt stehen. Er wird gebraucht, wenn der Satz
 * scheitert: Dann liegt das alte Buch noch da, und `nachFehler` findet es hier
 * wieder. Der alte Fehlertext dagegen fällt weg — er gehörte zum vorigen
 * Versuch, und stehen zu bleiben, während der nächste läuft, hieße, einen
 * Fehlschlag zweimal zu melden.
 */
export function setztJetzt(vorher: Bucheintrag | null | undefined): Bucheintrag {
  return { zustand: 'setzt', stand: bucheintrag(vorher).stand ?? null, fehler: null };
}

/** Der Satz ist durch: Es liegt ein Buch, und wir kennen seine Kennzahlen. */
export function nachErzeugung(stand: Reisebuchstand): Bucheintrag {
  return { zustand: 'liegt', stand, fehler: null };
}

/**
 * Der Satz ist gescheitert — und was das für den Knopf heißt.
 *
 * Lag vorher ein Buch, liegt es **danach unverändert da**: Der Server schreibt
 * die Datei erst, wenn sie fertig ist, und benennt sie dann um (`os.replace`);
 * ein Fehler fällt davor. Den Öffnen-Knopf jetzt zu verstecken wäre eine Lüge
 * in die unangenehme Richtung — sie nähme jemandem ein Buch weg, das er hat.
 *
 * Lag keines, bleibt `fehler`: Der Hauptknopf sagt wieder `Reisebuch erzeugen`,
 * und darüber steht der Satz des Servers.
 */
export function nachFehler(
  vorher: Bucheintrag | null | undefined,
  text: string
): Bucheintrag {
  const stand = bucheintrag(vorher).stand ?? null;
  return stand
    ? { zustand: 'liegt', stand, fehler: text }
    : { zustand: 'fehler', stand: null, fehler: text };
}

/**
 * Der Beizettel aus einem 409 — oder `null`.
 *
 * Der Server schickt bei einem schon gesetzten Buch ein Objekt statt eines
 * Satzes: `{ meldung, seiten, bytes, erzeugt_am, vorlage, illustration }`. Was
 * davon brauchbar ist, wandert in den Stand; was nicht, fällt weg und wird
 * nicht zur Null — dieselbe Regel wie in `buchzeile`, und aus demselben Grund:
 * Es ist JSON aus dem Netz und nichts davon zugesichert.
 *
 * `null`, wenn gar keine Zahl übrig bleibt. Dann ist es kein Beizettel,
 * sondern nur ein Objekt, und die Meldung allein trägt die Auskunft.
 */
function standAus(detail: unknown): Reisebuchstand | null {
  if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return null;
  const d = detail as Record<string, unknown>;
  const zahl = (roh: unknown) =>
    typeof roh === 'number' && Number.isFinite(roh) ? roh : 0;
  const wort = (roh: unknown) =>
    typeof roh === 'string' && roh.trim() ? roh : null;

  const stand: Reisebuchstand = {
    seiten: zahl(d.seiten),
    bytes: zahl(d.bytes),
    erzeugt_am: wort(d.erzeugt_am),
    vorlage: wort(d.vorlage),
    illustration: wort(d.illustration)
  };
  // Die Angaben seit dem 7.10.2026 — nur, wenn sie wirklich dastehen. Ein
  // Beizettel von vorher hat sie nicht, und ein erfundenes `vollstaendig`
  // wäre eine Auskunft über ein Buch, das sie nie getroffen hat.
  if (d.betriebsart === 'auswahl' || d.betriebsart === 'vollstaendig') {
    stand.betriebsart = d.betriebsart;
  }
  const fassungen = Array.isArray(d.fassungen)
    ? d.fassungen
    : d.fassungen && typeof d.fassungen === 'object'
      ? Object.keys(d.fassungen)
      : null;
  if (fassungen) stand.fassungen = FASSUNGEN.filter((f) => fassungen.includes(f));
  if (d.auswahl && typeof d.auswahl === 'object') stand.auswahl = auswahlAus(d.auswahl);
  if (typeof d.collage === 'boolean') stand.collage = d.collage;
  for (const feld of ['vorspann', 'titel', 'untertitel'] as const) {
    const wert = titelfeld(d[feld]);
    if (wert) stand[feld] = wert;
  }
  const fazit = fazitwahl(d.fazit_eintrag_id);
  if (fazit !== null) stand.fazit_eintrag_id = fazit;
  const fazitTitel = wort(d.fazit_titel);
  if (fazitTitel) stand.fazit_titel = fazitTitel.trim();
  const quelle = wort(d.zitate_quelle);
  if (quelle) stand.zitate_quelle = quelle.trim();
  const je = d.je_fassung;
  if (je && typeof je === 'object' && !Array.isArray(je)) {
    const angaben: Partial<Record<Fassung, Fassungsangabe>> = {};
    for (const f of FASSUNGEN) {
      const roh = (je as Record<string, unknown>)[f];
      if (!roh || typeof roh !== 'object' || Array.isArray(roh)) continue;
      const a = roh as Record<string, unknown>;
      const angabe: Fassungsangabe = {};
      if (typeof a.seiten === 'number' && Number.isFinite(a.seiten) && a.seiten > 0) angabe.seiten = a.seiten;
      if (typeof a.bytes === 'number' && Number.isFinite(a.bytes) && a.bytes > 0) angabe.bytes = a.bytes;
      angaben[f] = angabe;
    }
    stand.je_fassung = angaben;
  }
  return buchzeile(stand) ? stand : null;
}

/**
 * Der Beizettel aus `GET /reisebuch/stand` — oder `null`.
 *
 * Dieselbe Lesart wie beim 409-Objekt (`standAus`), und das ist Absicht: Es
 * ist derselbe Zettel auf zwei Wegen, und zwei Lesarten wären zwei
 * Gelegenheiten, ihn verschieden zu verstehen.
 *
 * Seit dem 7.10.2026 steht der Zettel in der Antwort unter `buch` (daneben
 * `lauf`); die alte, flache Form wird weiter gelesen — siehe `standantwort`.
 */
export function beizettel(roh: unknown): Reisebuchstand | null {
  return standantwort(roh).buch;
}

/** Die Meldung aus einem 409-Objekt — oder `null`. */
function meldungAus(detail: unknown): string | null {
  if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return null;
  const roh = (detail as Record<string, unknown>).meldung;
  return typeof roh === 'string' && roh.trim() ? roh : null;
}

/**
 * Der Server hat den Satz abgelehnt — und was das für die Zeile heißt.
 *
 * Zwei Absagen sehen im Netz gleich aus und meinen das Gegenteil voneinander;
 * sie hier auseinanderzuhalten ist der ganze Zweck dieser Funktion, und sie
 * steht deshalb hier und nicht verstreut im Markup.
 *
 * **`detail` ist ein Objekt** — dann liegt bereits ein Buch, und das Objekt
 * ist sein Beizettel. Das ist kein Fehlschlag: Der nächste sinnvolle Schritt
 * heißt öffnen, und genau dorthin geht der Zustand.
 *
 * Die Meldung des Servers fällt dabei **weg**, sobald der Beizettel etwas
 * hergibt — und das ist Absicht. `fehler` wird in der Fläche rot gesetzt (es
 * ist der Platz für den 503, der auch neben einem liegenden Buch gilt), und
 * „liegt bereits eines" ist keine Störung. Gesagt hat es dann ohnehin die
 * Zeile darunter: Vorlage, Seiten und ein Zeitpunkt, der erkennbar nicht von
 * diesem Klick stammt. Gibt der Beizettel nichts her, bleibt die Meldung —
 * dann ist sie die einzige Auskunft, die es gibt.
 *
 * **`detail` ist ein Satz** — dann hat der Server einen Grund genannt: Die
 * Reise ist nicht abgeschlossen (409), die Vorlage kennt er nicht (422), der
 * Satz ist misslungen (503). Alle drei gehören unverändert in die Fläche und
 * führen über `nachFehler`, also zurück zum Knopf.
 *
 * **Sonst** — eine Liste (so meldet FastAPI seine eigenen Prüffehler), gar
 * nichts — gilt `ersatz`. Ein `[object Object]` an einer Reisezeile wäre
 * schlechter als ein eigener, ehrlicher Satz.
 *
 * Beim Objekt entscheidet die **Form** und nicht der Inhalt: Ein 409 mit
 * Objekt heißt „liegt schon eines", auch wenn die Zahlen darin unbrauchbar
 * sind. Dann steht eben keine Zeile darunter — der Knopf öffnet trotzdem, und
 * das ist die Auskunft, auf die es ankommt.
 */
export function nachAblehnung(
  vorher: Bucheintrag | null | undefined,
  detail: unknown,
  ersatz: string
): Bucheintrag {
  if (typeof detail === 'string') {
    return nachFehler(vorher, detail.trim() || ersatz);
  }
  if (!detail || typeof detail !== 'object' || Array.isArray(detail)) {
    return nachFehler(vorher, ersatz);
  }
  // Der Beizettel, sonst das, was diese Sitzung schon wusste.
  const stand = standAus(detail) ?? bucheintrag(vorher).stand ?? null;
  return {
    zustand: 'liegt',
    stand,
    fehler: stand ? null : (meldungAus(detail) ?? ersatz)
  };
}

/**
 * Beim Öffnen kam 404: Das Buch ist weg.
 *
 * Das passiert wirklich — eine zweite Sitzung, ein geleerter Ordner, eine
 * Reise, die zwischendurch im Papierkorb war. Der Zustand fällt deshalb auf
 * `unbekannt` zurück und **nicht** auf `fehler`: Das ist kein Fehlschlag, den
 * man wiederholen sollte, sondern ein Buch, das es nicht mehr gibt. Der
 * nächste sinnvolle Schritt heißt erzeugen, und genau das steht dann auf dem
 * Knopf. Der Satz des Servers bleibt daneben stehen, damit niemand rätselt,
 * warum der Knopf zurückgesprungen ist.
 */
export function nachVerlust(text: string): Bucheintrag {
  return { zustand: 'unbekannt', stand: null, fehler: text };
}

/**
 * Das liegende Buch ist gelöscht, der neue Satz läuft — `DELETE` war durch.
 *
 * Der Zustand bleibt `setzt` (der Knopf bleibt gesperrt), aber der **Stand
 * fällt weg**. Das ist der ganze Zweck dieser Funktion: Scheitert danach der
 * POST, führt `nachFehler` sonst über den alten Stand zurück nach `liegt` —
 * und die Zeile böte zwei Öffnen-Knöpfe für ein Buch an, das es seit einer
 * Sekunde nicht mehr gibt. So landet sie bei `fehler` und beim Erzeugen.
 */
export function nachRaeumen(): Bucheintrag {
  return { zustand: 'setzt', stand: null, fehler: null };
}

/**
 * Die Maske hat den Beizettel geholt — was das für die Zeile heißt.
 *
 * Der Abruf fällt ohnehin an (die Maske belegt sich damit vor), und er
 * beantwortet nebenbei die Frage, die diese Datei sonst nie stellt: Liegt
 * schon eines? Ja — dann steht die Zeile auf `liegt`, und wer die Maske
 * abbricht, findet seine Öffnen-Knöpfe. Nein (`null`, also 404) — dann
 * fällt ein `liegt` dieser Sitzung auf `unbekannt` zurück, still: Die Maske
 * ist offen, und eine rote Zeile dahinter wäre die falsche Bühne.
 *
 * Während eines Satzes ändert sich nichts — der Ausgang gehört dem POST.
 */
export function nachStand(
  vorher: Bucheintrag | null | undefined,
  stand: Reisebuchstand | null
): Bucheintrag {
  const v = bucheintrag(vorher);
  if (v.zustand === 'setzt') return v;
  if (stand) return { zustand: 'liegt', stand, fehler: v.fehler ?? null };
  return v.zustand === 'liegt' ? { zustand: 'unbekannt', stand: null, fehler: null } : v;
}

/**
 * Muss vor dem POST gelöscht werden?
 *
 * Ja, wenn diese Sitzung ein Buch kennt — aus der Zeile (`liegt`) oder aus
 * dem Beizettel, den die Maske gerade geholt hat. Sonst nicht: Ein DELETE
 * auf Verdacht kostet nichts (404 wird geschluckt), aber er stünde in jedem
 * ersten Satz als Anfrage ohne Gegenstand.
 */
export function mussRaeumen(vorher: Bucheintrag | null | undefined, standDerMaske: boolean): boolean {
  return standDerMaske || bucheintrag(vorher).zustand === 'liegt';
}

/* ══ Der Satzlauf im Hintergrund (seit dem 7.10.2026) ═════════════════════ */

/**
 * Was der Server über den Lauf sagt, der das Buch setzt.
 *
 * Die Form ist die von `_reisebuch_lauf` im Backend:
 * `{laeuft, schritt, von, bis, fehler, beendet_um}`. `schritt` ist ein **Wort**
 * (`Daten lesen`, `Bildschirmfassung setzen`, `Druckfassung setzen`,
 * `ablegen`), `von` zählt die **erledigten** Schritte, `bis` alle. Die
 * Antwort des POST trägt dazu `hinweis` und weder `fehler` noch `beendet_um`.
 */
export interface Laufstand {
  laeuft: boolean;
  /** Der Schritt, an dem der Lauf gerade arbeitet — oder an dem er scheiterte. */
  schritt: string | null;
  /** Erledigte Schritte. */
  von: number | null;
  /** Alle Schritte. */
  bis: number | null;
  /** Der Satz des Servers, wenn der Lauf gescheitert ist. */
  fehler: string | null;
  /** Hauszeit ohne Zone, `null`, solange er läuft. */
  beendet_um: string | null;
  /** Nur in der Antwort des POST: ein Satz zum Lauf. */
  hinweis: string | null;
}

/** Die Antwort von `GET /reisebuch/stand`, gelesen. */
export interface Standantwort {
  buch: Reisebuchstand | null;
  lauf: Laufstand | null;
}

/**
 * Wie oft das Panel nachfragt, solange gesetzt wird.
 *
 * Drei Sekunden: Ein Schritt dauert bei Reise 19 eine halbe Minute und mehr,
 * und der Server sagt ausdrücklich, dass die Frage nichts kostet. Öfter zu
 * fragen ließe den Satz nicht schneller laufen.
 */
export const ABFRAGE_MS = 3000;

/**
 * Wie viele Abfragen hintereinander scheitern dürfen, bevor das Panel aufhört.
 *
 * Eine einzelne verlorene Antwort ist kein Grund, den Fortschritt fallen zu
 * lassen — fünf hintereinander sind einer, und ein Panel, das danach still
 * weiterfragt, fragt womöglich einen Server, der nicht mehr da ist.
 */
export const ABFRAGE_FEHLVERSUCHE = 5;

/**
 * Der Satz zu einem 504.
 *
 * Bis zum 7.10.2026 stand an dieser Stelle nur `504 Gateway Time-out`. Das
 * ist eine Statuszeile und keine Auskunft — und sie war außerdem falsch
 * verstanden, denn das Buch entstand trotzdem. Der Satz sagt beides: Die
 * Antwort fehlt, das Buch womöglich nicht.
 */
export const ZEIT_SATZ =
  'Der Server hat nicht rechtzeitig geantwortet — das Buch wird womöglich trotzdem gesetzt, gleich noch einmal nachsehen.';

/**
 * Der Satz, wenn ein Lauf ohne Spur verschwindet.
 *
 * Die Buchführung des Servers lebt im Prozess (`hintergrund.py`): Nach einem
 * Neustart kennt er den Lauf nicht mehr, und ein abgebrochener Satz hat kein
 * Buch hinterlassen. Das Panel sieht dann ein 404, wo eben noch ein Fortschritt
 * stand — und ein stilles Zurückspringen auf „Reisebuch setzen“ sähe aus, als
 * hätte es den Klick nie gegeben.
 */
export const ABBRUCH_SATZ =
  'Der Satz ist abgebrochen, ohne ein Buch zu hinterlassen — womöglich wurde der Server neu gestartet. Bitte noch einmal setzen.';

/** Ist das die Zeitüberschreitung des Vorschalt-Servers? */
export function istZeitueberschreitung(status: number | null | undefined): boolean {
  return status === 504;
}

/**
 * Der Fehlertext zu einer abgelehnten Anfrage — ein Satz, keine Statuszeile.
 *
 * Bei 504 der eigene Satz (`ZEIT_SATZ`): Dort kommt die Antwort von nginx und
 * nicht vom Server, und `text` ist dann `504 Gateway Time-out`. Sonst der Satz
 * des Servers, und wo keiner ist, `ersatz`.
 */
export function fehlersatz(
  status: number | null | undefined,
  text: string | null | undefined,
  ersatz: string
): string {
  if (istZeitueberschreitung(status)) return ZEIT_SATZ;
  return typeof text === 'string' && text.trim() ? text : ersatz;
}

/**
 * Einen Laufstand aus dem Netz lesen — oder `null`.
 *
 * Ohne ein `laeuft` als Wahrheitswert ist es kein Laufstand: Ob gerade
 * gesetzt wird, ist die eine Angabe, auf die es ankommt, und eine geratene
 * wäre schlimmer als keine. Zahlen, die keine ganzen, nichtnegativen sind,
 * fallen weg; leere Wörter auch.
 */
export function laufAus(roh: unknown): Laufstand | null {
  if (!roh || typeof roh !== 'object' || Array.isArray(roh)) return null;
  const d = roh as Record<string, unknown>;
  if (typeof d.laeuft !== 'boolean') return null;
  const zahl = (x: unknown) =>
    typeof x === 'number' && Number.isInteger(x) && x >= 0 ? x : null;
  const wort = (x: unknown) => (typeof x === 'string' && x.trim() ? x.trim() : null);
  return {
    laeuft: d.laeuft,
    schritt: wort(d.schritt),
    von: zahl(d.von),
    bis: zahl(d.bis),
    fehler: wort(d.fehler),
    beendet_um: wort(d.beendet_um),
    hinweis: wort(d.hinweis)
  };
}

/**
 * Eine Antwort des Servers zum Buch lesen — welche Form sie auch hat.
 *
 * Drei Formen, und alle drei kommen vor:
 *
 * * `{buch, lauf}` — `GET /reisebuch/stand` seit dem 7.10.2026.
 * * `{laeuft, schritt, von, bis, hinweis}` — die Antwort des POST (202).
 * * der flache Beizettel — `GET /reisebuch/stand` bis zum 7.10.2026 und der
 *   201 des alten POST. Ein Server, der noch nicht umgestellt ist, bekommt so
 *   dieselbe Oberfläche wie einer, der es ist.
 */
export function standantwort(roh: unknown): Standantwort {
  if (!roh || typeof roh !== 'object' || Array.isArray(roh)) return { buch: null, lauf: null };
  const d = roh as Record<string, unknown>;
  if ('buch' in d || 'lauf' in d) return { buch: standAus(d.buch), lauf: laufAus(d.lauf) };
  if (typeof d.laeuft === 'boolean') return { buch: null, lauf: laufAus(d) };
  return { buch: standAus(d), lauf: null };
}

/**
 * Was eine Antwort zum Lauf für den Zustand heißt.
 *
 * Die Reihenfolge ist die Aussage:
 *
 * 1. **Es läuft** — `setzt`, ohne Fehler. Ein alter Fehlertext (etwa der
 *    504-Satz) ist damit beantwortet: Das Buch wird ja gesetzt.
 * 2. **Gescheitert** — der Satz des Servers. Lag (aus welchem Grund auch
 *    immer) ein Buch, bleibt es in der Hand; sonst `fehler`, und der Knopf
 *    lädt wieder zum Setzen ein.
 * 3. **Ein Buch liegt** — `liegt`.
 * 4. **Nichts** (404). Lief eben noch ein Satz, ist er verschwunden
 *    (`ABBRUCH_SATZ`); sonst bleibt, was diese Sitzung schon wusste — auch
 *    der 504-Satz, denn dann hat das Nachsehen noch nichts gefunden.
 */
export function nachLauf(
  vorher: Bucheintrag | null | undefined,
  a: Standantwort
): Bucheintrag {
  const v = bucheintrag(vorher);
  if (a.lauf?.laeuft) return { zustand: 'setzt', stand: a.buch ?? null, fehler: null };
  if (a.lauf?.fehler) {
    return a.buch
      ? { zustand: 'liegt', stand: a.buch, fehler: a.lauf.fehler }
      : { zustand: 'fehler', stand: null, fehler: a.lauf.fehler };
  }
  if (a.buch) return { zustand: 'liegt', stand: a.buch, fehler: null };
  if (v.zustand === 'setzt') return { zustand: 'fehler', stand: null, fehler: ABBRUCH_SATZ };
  return {
    zustand: v.zustand === 'fehler' ? 'fehler' : 'unbekannt',
    stand: null,
    fehler: v.fehler ?? null
  };
}

/** Was der Fortschritt anzeigt: ein Satz und, wo es Zahlen gibt, ein Balken. */
export interface Fortschritt {
  satz: string;
  /** Erledigte Schritte — `null`, wenn der Server keine Zahlen nennt. */
  wert: number | null;
  max: number | null;
}

/**
 * Der Fortschritt eines Laufs: `Schritt 2 von 4: Bildschirmfassung setzen`.
 *
 * Gezählt wird der Schritt, an dem gearbeitet wird — also einer mehr als die
 * erledigten, und nie mehr als alle. Bis zum 7.10.2026 stand hier mit Absicht
 * kein Fortschritt („setzt …“), weil der Server keinen meldete und ein
 * kriechender Balken eine erfundene Auskunft gewesen wäre. Jetzt meldet er
 * einen, und der Balken steht genau dort, wo der Server steht.
 */
export function fortschritt(lauf: Laufstand | null | undefined): Fortschritt {
  const text = lauf?.schritt ? lauf.schritt.charAt(0).toUpperCase() + lauf.schritt.slice(1) : null;
  const von = lauf?.von ?? null;
  const bis = lauf?.bis ?? null;
  if (bis !== null && bis > 0 && von !== null) {
    const nummer = Math.min(von + 1, bis);
    return {
      satz: `Schritt ${nummer} von ${bis}${text ? `: ${text}` : ''}`,
      wert: Math.min(von, bis),
      max: bis
    };
  }
  return { satz: text ? `${text} …` : 'Das Reisebuch wird gesetzt …', wert: null, max: null };
}

/** Die zwei Umfänge, wie die Auswahlmaske sie nennt. */
export const UMFANGSTITEL: Readonly<Record<Betriebsart, string>> = {
  vollstaendig: 'Das ganze Buch',
  auswahl: 'Meine Auswahl'
};

/**
 * Die Seiten je Fassung: `Bildschirm 48 Seiten · Druck 52 Seiten`.
 *
 * Es stehen die Fassungen da, die es gibt (`oeffnungsknoepfe`), und von denen
 * nur die, deren Seitenzahl bekannt ist. Für die Bildschirmfassung steht im
 * Zweifel `seiten` ein — die Zahl, die der Beizettel immer schon trug.
 */
export function seitenJeFassung(stand: Reisebuchstand | null | undefined): string | null {
  if (!stand) return null;
  const teile: string[] = [];
  for (const f of oeffnungsknoepfe(stand)) {
    let n = stand.je_fassung?.[f]?.seiten ?? null;
    if (n === null && f === 'bildschirm') n = stand.seiten;
    if (typeof n === 'number' && Number.isFinite(n) && n > 0) {
      teile.push(`${FASSUNGSTITEL[f]} ${anzahl(Math.round(n), 'Seite', 'Seiten')}`);
    }
  }
  return teile.length ? teile.join(' · ') : null;
}

/** Eine Angabe im Stand des Panels — Etikett und Wert. */
export interface Standangabe {
  etikett: string;
  wert: string;
}

/**
 * Der Stand eines liegenden Buchs, wie das Panel ihn zeigt.
 *
 * Titel, Vorlage, Umfang, Fazit, Zitate, Datum, Seiten je Fassung — in
 * dieser Reihenfolge, und was
 * fehlt, entfällt (dieselbe Regel wie in `buchzeile`). Der Umfang fehlt bei
 * Büchern von vor dem 7.10.2026: Sie waren vollständig, aber der Beizettel
 * sagt es nicht, und eine erfundene Angabe wäre eine über ein Buch, das sie
 * nie getroffen hat.
 *
 * `fazittitel` ist der Titel des Fazit-Eintrags, den der Aufrufer im
 * Tagebuch nachgeschlagen hat — für einen Beizettel, der nur die Kennung
 * trägt (siehe `fazitangabe`).
 */
export function standangaben(
  stand: Reisebuchstand | null | undefined,
  vorlagen?: readonly Vorlage[] | null,
  fazittitel?: string | null
): Standangabe[] {
  if (!stand) return [];
  const angaben: Standangabe[] = [];
  const titel = titelzeile(stand);
  if (titel) angaben.push({ etikett: 'Titel', wert: titel });
  const vorlage = vorlagentitel(stand.vorlage, vorlagen);
  if (vorlage) angaben.push({ etikett: 'Vorlage', wert: vorlage });
  if (stand.betriebsart) angaben.push({ etikett: 'Umfang', wert: UMFANGSTITEL[stand.betriebsart] });
  const fazit = fazitangabe(stand, fazittitel);
  if (fazit) angaben.push({ etikett: 'Fazit', wert: fazit });
  const zitate = zitatangabe(stand.zitate_quelle);
  if (zitate) angaben.push({ etikett: 'Zitate', wert: zitate });
  const wann = datumZeit(stand.erzeugt_am);
  if (wann) angaben.push({ etikett: 'Gesetzt', wert: wann });
  const seiten = seitenJeFassung(stand);
  if (seiten) angaben.push({ etikett: 'Seiten', wert: seiten });
  return angaben;
}

/**
 * Der gesetzte Titel in einer Zeile: `Siebzehn Tage Italien · Von Turin bis
 * Rom · Untertitel` — was fehlt, entfällt. `null` bei einem Buch, dessen
 * Beizettel keine Titelfelder kennt (gesetzt vor dem 9.10.2026 oder ohne
 * eigene Felder): Dann trägt es den Reisenamen, und den zeigt die Fläche
 * ohnehin.
 */
export function titelzeile(stand: Reisebuchstand | null | undefined): string | null {
  if (!stand) return null;
  const teile = [stand.vorspann, stand.titel, stand.untertitel]
    .map((t) => titelfeld(t))
    .filter((t) => t);
  return teile.length ? teile.join(' · ') : null;
}

/**
 * Das Fazit im Stand: der Titel des gewählten Eintrags oder „kein Fazit“.
 *
 * Kennt der Beizettel nur die Kennung, steht die Zeile erst da, wenn der
 * Aufrufer den Titel nachgeschlagen hat (`fazittitel`) — eine nackte Nummer
 * sagt niemandem etwas. Ohne Kennung, aber mit Titel hat der Server den
 * Eintrag selbst gewählt; dann steht das dabei. `null` bei einem Buch, das
 * über sein Fazit nichts sagt.
 */
export function fazitangabe(
  stand: Reisebuchstand | null | undefined,
  fazittitel?: string | null
): string | null {
  if (!stand) return null;
  const id = fazitwahl(stand.fazit_eintrag_id);
  if (id === 0) return FAZIT_KEINS;
  const titel = (stand.fazit_titel ?? fazittitel ?? '').trim();
  if (!titel) return null;
  return id === null ? `${titel} (automatisch gewählt)` : titel;
}

/**
 * Woher die Großzitate kamen, als Satzteil für den Stand — `null`, wenn der
 * Beizettel es nicht sagt. Eine unbekannte Quelle steht, wie sie kommt.
 */
export function zitatangabe(quelle: string | null | undefined): string | null {
  const q = (quelle ?? '').trim();
  if (!q) return null;
  if (q.toLowerCase() === 'lotte') return 'von Lotte gewählt';
  if (q.toLowerCase() === 'regel') return 'nach der festen Regel gewählt';
  return q;
}
